import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, office } from "@/lib/api";
import { ago } from "@/lib/format";
import { OFFICE_ROLE_LABEL, useSession, type OfficeRole } from "@/lib/session";
import { useTill } from "@/lib/till-context";
import { Avatar, Button, Field, Input, Select, cx, useConfirm, useToast } from "@/components/ui";
import { Card, PageHead } from "@/components/Layout";

type Member = { id: string; name: string; email: string | null; phone: string | null; role: OfficeRole; platform: boolean; lastSeenAt: number | null };
type Invite = { id: string; code: string; name: string; login: string; role: OfficeRole; invitedBy: string; expired: boolean; createdAt: number; expiresAt: number };

const ROLES: { id: OfficeRole; sub: string }[] = [
  { id: "gm", sub: "Everything, adds staff below them" },
  { id: "manager", sub: "Runs the shop day to day" },
  { id: "accountant", sub: "Reports and money owed" },
  { id: "stock", sub: "Receives and counts stock" },
  { id: "owner", sub: "Everything, adds people" },
];
const CAN: [string, string[]][] = [
  ["Dashboard and reports", ["Yes", "Yes", "Yes", "Yes", "Yes"]],
  ["Products, prices and import", ["Yes", "Yes", "Yes", "See only", "See only"]],
  ["Receive, count and move stock", ["Yes", "Yes", "Yes", "No", "Yes"]],
  ["Customers, credit and money owed", ["Yes", "Yes", "Yes", "Yes", "See only"]],
  ["Till staff, tills and settings", ["Yes", "Yes", "Yes", "No", "No"]],
  ["Team (this page)", ["Yes", "Below them", "No", "No", "No"]],
];

/** An owner manages everyone; a general manager only managers, accountants and stock keepers. */
const canManage = (me: OfficeRole, role: OfficeRole) => me === "owner" || (me === "gm" && role !== "owner" && role !== "gm");
const joinLink = (code: string) => `${window.location.origin}/join/${code}`;
const wa = (login: string, text: string) => (/^0\d{9}$/.test(login) ? `https://wa.me/${login.replace(/^0/, "233")}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`);

/** Who signs in to the back office, and with what role. The owner invites people by link. */
export function Team() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const s = useSession();
  const { store, me } = useTill();
  const myRole: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const q = useQuery({ queryKey: ["team"], queryFn: () => api<{ members: Member[]; invites: Invite[] }>(office("/team")) });
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [role, setRole] = useState<OfficeRole>("manager");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Invite | null>(null);
  const shop = store?.storeName || "the shop";
  const message = (i: Pick<Invite, "name" | "role" | "code">) => `${s.user?.name ?? "The owner"} added you to the ${shop} back office as ${OFFICE_ROLE_LABEL[i.role].toLowerCase()}. Set your password here (works once, for 48 hours): ${joinLink(i.code)}`;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["team"] });

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api<{ invite: Invite }>(office("/team/invites"), { body: { name, login, role } });
      setMade(r.invite);
      setName("");
      setLogin("");
      refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't make the invite", "err");
    } finally {
      setBusy(false);
    }
  };
  const act = async (path: string, body: unknown, done: string) => {
    try {
      const r = await api<{ invite?: Invite }>(office(path), { body });
      toast(done);
      refresh();
      return r;
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  const copy = (text: string) => void navigator.clipboard?.writeText(text).then(() => toast("Link copied"));

  const members = (q.data?.members ?? []).filter((m) => !m.platform);
  const invites = q.data?.invites ?? [];
  return (
    <>
      <PageHead title="Team" sub="People who sign in to this back office. Till staff use a PIN instead (Staff & roles)." />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex flex-col gap-4">
          <Card title="People">
            <div className="flex flex-col">
              {q.isLoading ? <p className="px-5 pb-5 text-sm text-slate-500">Loading…</p> : null}
              {members.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-5 py-3.5 text-sm">
                  <Avatar name={m.name} color="#f2c3a0" size={36} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <b className="truncate">
                      {m.name}
                      {m.id === s.user?.id ? <span className="font-normal text-slate-500"> (you)</span> : null}
                    </b>
                    <span className="truncate text-xs text-slate-500">
                      {m.email ?? m.phone} · {m.lastSeenAt ? `last in ${ago(m.lastSeenAt)}` : "not signed in yet"}
                    </span>
                  </span>
                  <Select aria-label={`Role for ${m.name}`} value={m.role} disabled={m.id === s.user?.id || !canManage(myRole, m.role)} onChange={(e) => void act(`/team/${m.id}`, { role: e.target.value }, "Role changed")} className="!h-10 !w-48">
                    {ROLES.filter((r) => r.id === m.role || canManage(myRole, r.id)).map((r) => (
                      <option key={r.id} value={r.id}>
                        {OFFICE_ROLE_LABEL[r.id]}
                      </option>
                    ))}
                  </Select>
                  {m.id !== s.user?.id && canManage(myRole, m.role) ? (
                    <Button
                      size="sm"
                      tone="danger"
                      onClick={async () => {
                        if (await confirm({ title: `Remove ${m.name}?`, body: "They can't sign in to the back office any more. Their past work stays in the reports.", ok: "Remove", danger: true })) void act(`/team/${m.id}`, { remove: true }, `${m.name} removed`);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ))}
              {invites.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-amber-50/60 px-5 py-3.5 text-sm">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-full border-2 border-dashed border-slate-300 text-xs font-bold text-slate-500">{i.name.slice(0, 2).toUpperCase()}</span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <b className="truncate">{i.name}</b>
                    <span className="truncate text-xs text-slate-500">
                      {i.login} · {OFFICE_ROLE_LABEL[i.role]} · {i.expired ? "invite ran out" : `invited ${ago(i.createdAt)}, not joined yet`}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const r = await act(`/team/invites/${i.id}/resend`, {}, "New link made");
                      if (r?.invite) setMade(r.invite);
                    }}
                  >
                    {i.expired ? "New link" : "Resend"}
                  </Button>
                  <Button size="sm" onClick={() => void act(`/team/invites/${i.id}/cancel`, {}, "Invite cancelled")}>
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          </Card>
          <Card title="What each role can do">
            <div className="overflow-x-auto px-5 pb-5">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500">
                    <th className="py-2 font-semibold" />
                    {["owner", "gm", "manager", "accountant", "stock"].map((r) => (
                      <th key={r} className="py-2 text-center font-semibold">
                        {OFFICE_ROLE_LABEL[r as OfficeRole]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CAN.map(([what, cells]) => (
                    <tr key={what} className="border-t border-slate-100">
                      <td className="py-2">{what}</td>
                      {cells.map((c, i) => (
                        <td key={i} className={cx("py-2 text-center", c === "No" && "text-slate-400")}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <Card title={made ? `Invite for ${made.name}` : "Add a person"}>
          {made ? (
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <p className="rounded-lg bg-[#e7f5e4] px-3 py-3 leading-relaxed">{message(made)}</p>
              <a href={wa(made.login, message(made))} target="_blank" rel="noreferrer" className="grid h-12 place-items-center rounded-xl bg-brand font-bold text-white">
                Send on WhatsApp
              </a>
              <Button onClick={() => copy(joinLink(made.code))}>Copy the link</Button>
              <span className="text-xs text-slate-500">The link works once and runs out in 48 hours. They choose their own password; nobody else sees it.</span>
              <Button onClick={() => setMade(null)}>Add someone else</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 px-5 pb-5">
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Yaw Boakye" maxLength={80} />
              </Field>
              <Field label="Phone or email" hint="What they'll sign in with">
                <Input value={login} onChange={(e) => setLogin(e.target.value)} placeholder="020 555 7781 or name@shop.com" />
              </Field>
              <span className="text-[13px] font-semibold">Role</span>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.filter((r) => canManage(myRole, r.id)).map((r) => (
                  <button key={r.id} type="button" aria-pressed={role === r.id} onClick={() => setRole(r.id)} className={cx("flex flex-col items-start gap-0.5 border-[1.5px] px-3 py-2.5 text-left", role === r.id ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}>
                    <b className="text-sm">{OFFICE_ROLE_LABEL[r.id]}</b>
                    <span className="text-xs text-slate-500">{r.sub}</span>
                  </button>
                ))}
              </div>
              <Button tone="primary" size="lg" disabled={busy || !name.trim() || !login.trim()} onClick={() => void invite()} className="mt-1">
                {busy ? "Making the invite…" : "Make an invite link"}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
